import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import type { CreateExecutiveMatterRequest, ExecutiveMatterDto, ExecutiveMattersResponse, ProjectListResponse, SetExecutiveMatterStateRequest, UpdateExecutiveMatterRequest } from '../../shared/api-types.js';
import { EXECUTIVE_MATTER_STATUSES, EXECUTIVE_MATTER_STATUS_LABELS, TODO_PRIORITIES, TODO_PRIORITY_LABELS, TODO_PRIORITY_RANK, type ExecutiveMatterStatus, type TodoPriority } from '../../shared/enums.js';
import { api, type ApiFailure } from '../api.js';
import { PATHS } from '../paths.js';
import { useMe } from '../shell/me-context.js';
import {
  Button,
  ConfirmDialog,
  DataTable,
  Dialog,
  EM_DASH,
  FormErrorSummary,
  Icon,
  IconButton,
  LinkButton,
  Menu,
  Page,
  PageHead,
  ProjectChip,
  SelectField,
  StateView,
  StatusPill,
  TextField,
  TextareaField,
  failureDetail,
  pluralise,
  useRequest,
  useToast,
  type DataTableColumn,
  type MenuEntry,
} from '../ui/index.js';
import { AuthorLine, ExecutiveFailure, ExecutiveStrip } from './executive-parts.js';
import {
  ADD_MATTER_DESCRIPTION,
  EDIT_MATTER_DESCRIPTION,
  MANUAL_MATTER_NOTE,
  MATTER_FILTER_LABELS,
  MATTER_STATUS_FILTERS,
  MATTER_TEXT_LOCKED,
  MATTER_TEXT_MAX,
  completedByNamesSentence,
  completedByText,
  matterEmpty,
  matterFromSortValue,
  matterFromText,
  matterStatusToast,
  parseMatterFilter,
} from './lib/executive.js';
import { failureSentence } from './project-filing.js';
import { OWN_PROJECTS_PATH } from './project-checkboxes.js';
import { DueDate, PriorityPill, useToday } from './todo-table.js';

// The Matters section (ADR 0019; design §2.7 and §2.13 "Matters"; the owner's pick E10): ONE shared list of executive
// matters — a report's suggested actions and the matters executives add — with each executive's own word on each (Open,
// Completed, Dismissed: 05-content §6, as a to-do's) and their own filing under one of their own projects. Nobody's word
// changes another's: "Completed by 1 of 2" counts the executives who may read now and names them on expansion. The filter
// is in the URL (`?status=`), the sort under `matters_`; the view's own CSV carries the same rows (the Costs precedent:
// counts, never the names). Any executive sets a matter's priority and due date; a matter's words, and its deletion, are
// its creator's alone (the route answers 403 otherwise, and the page offers neither).

const STATUS_RANK = (status: ExecutiveMatterStatus): number => EXECUTIVE_MATTER_STATUSES.indexOf(status);

/**
 * The key a request's data was read for: the key current when that data arrived, kept until other data does. Why by the
 * data's identity (the stage 6 review): the render that follows a change of filter still holds the previous filter's data
 * and is not yet `refreshing` — the request's effect has not run — so only a change of the data itself says that the read
 * for the new key has landed (or that the page edited the list it holds, which belongs to the current key).
 */
function useReadFor<T, K>(data: T | null, key: K, refreshing: boolean): K | null {
  const seen = useRef<{ data: T | null; key: K | null }>({ data: null, key: null });
  if (data !== seen.current.data && !refreshing) seen.current = { data, key };
  return data === null ? null : seen.current.key;
}
const STATUS_TONE = { open: 'info', done: 'success', dismissed: 'neutral' } as const;

function MatterStatePill({ status }: { status: ExecutiveMatterStatus }) {
  return <StatusPill tone={STATUS_TONE[status]} icon={status === 'done' ? 'check' : status === 'dismissed' ? 'x' : 'circle'}>{EXECUTIVE_MATTER_STATUS_LABELS[status]}</StatusPill>;
}

export function MattersSection() {
  const me = useMe();
  const toast = useToast();
  const today = useToday();
  const [params, setParams] = useSearchParams();
  const filter = parseMatterFilter(params.get('status'));
  const projectsOn = me.integrations.projects !== 'off';
  const request = useRequest<ExecutiveMattersResponse>((signal) => api.get<ExecutiveMattersResponse>(`/api/executive/matters?status=${filter}`, { signal }), [filter]);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<ExecutiveMatterDto | null>(null);
  const [filing, setFiling] = useState<ExecutiveMatterDto | null>(null);
  const [deleting, setDeleting] = useState<ExecutiveMatterDto | null>(null);
  const [busy, setBusy] = useState<ReadonlySet<string>>(new Set());

  const replace = (matter: ExecutiveMatterDto): void => {
    // Why kept in place whatever the filter: a row changed under the reader's hand stays visible until they move on
    // (04-data §1.10, "the row does not vanish"); the next read of the list sorts it where it now belongs.
    request.setData((current) => (current ? { ...current, items: current.items.map((item) => (item.id === matter.id ? matter : item)) } : current));
  };

  const setStatus = async (matter: ExecutiveMatterDto, status: ExecutiveMatterStatus, undo = true): Promise<void> => {
    const previous = matter.mine.status;
    setBusy((current) => new Set(current).add(matter.id));
    const result = await api.patch<{ matter: ExecutiveMatterDto }>(`/api/executive/matters/${encodeURIComponent(matter.id)}/state`, { status } satisfies SetExecutiveMatterStateRequest);
    setBusy((current) => {
      const next = new Set(current);
      next.delete(matter.id);
      return next;
    });
    if (!result.ok) {
      toast.error(<>We couldn’t change “{matter.text}”. {failureDetail(result)}</>);
      return;
    }
    replace(result.data.matter);
    const message = matterStatusToast(matter.text, status, previous);
    if (undo) toast.success(message, { action: { label: 'Undo', onClick: () => void setStatus(result.data.matter, previous, false) } });
    else toast.success(message);
  };

  const filterControl = (
    <SelectField
      label="Show"
      value={filter}
      onChange={(event) => setParams((current) => {
        const next = new URLSearchParams(current);
        if (event.target.value === 'open') next.delete('status');
        else next.set('status', event.target.value);
        return next;
      }, { replace: true })}
      options={MATTER_STATUS_FILTERS.map((value) => ({ value, label: MATTER_FILTER_LABELS[value] }))}
      className="executive-matters__filter"
    />
  );

  const columns = useMemo<DataTableColumn<ExecutiveMatterDto>[]>(() => [
    {
      key: 'text',
      header: 'Matter',
      card: 'title',
      sortValue: (row) => row.text,
      cell: (row) => (
        <span className="stack stack--1">
          <span className="executive-matter__text">{row.text}</span>
          {row.why ? <span className="text-sm secondary">{row.why}</span> : null}
        </span>
      ),
    },
    {
      key: 'from',
      header: 'From',
      fold: 1,
      // Why not the words shown (the stage 6 review; 04-data §1.2.1: dates sort chronologically): "Report of 1 Oct" sorted
      // before "Report of 29 Sep" as text. A report's matters sort by the date the cell says, then those executives added.
      directionWords: { asc: 'reports first, earliest first', desc: 'added matters first, then the latest report' },
      sortValue: (row) => matterFromSortValue(row),
      cell: (row) => (row.from.kind === 'report' && row.from.reportReadable ? <Link to={PATHS.executiveReport(row.from.reportId)} className="hit-area">{matterFromText(row)}</Link> : matterFromText(row)),
    },
    { key: 'priority', header: 'Priority', type: 'enum', directionWords: { asc: 'highest first', desc: 'lowest first' }, fold: 4, sortValue: (row) => TODO_PRIORITY_RANK[row.priority], cell: (row) => <PriorityPill priority={row.priority} /> },
    { key: 'due_date', header: 'Due', type: 'date', fold: 5, sortValue: (row) => row.dueDate, cell: (row) => <DueDate dueDate={row.dueDate} status={row.mine.status} today={today} /> },
    { key: 'mine', header: 'My state', headerTitle: 'Your own word on this matter; each executive keeps their own', type: 'enum', directionWords: { asc: 'open first', desc: 'dismissed first' }, sortValue: (row) => STATUS_RANK(row.mine.status), cell: (row) => <MatterStatePill status={row.mine.status} /> },
    {
      key: 'completed_by',
      header: 'Completed by',
      type: 'number',
      // Why numeric (the stage 6 review; 04-data §1.1: numbers right, in tabular figures): its value is a count, "1 of 2".
      numeric: true,
      fold: 3,
      directionWords: { asc: 'fewest first', desc: 'most first' },
      sortValue: (row) => row.doneBy.count,
      // Why a disclosure for the names (design §2.13: "names on expansion"): the count compares rows; who is the detail.
      cell: (row) => (
        <details className="executive-completed-by">
          <summary>
            <span aria-hidden="true" className="num">{completedByText(row.doneBy)}</span>
            <span className="visually-hidden">{`Completed by ${completedByText(row.doneBy)} executives: show who`}</span>
            {/* Why a decorative glyph and not generated text: a screen reader would announce a CSS "▾"; the state is the
                summary's own expanded state. */}
            <Icon name="chevron-down" size={14} className="executive-completed-by__chevron" />
          </summary>
          <p className="text-sm">{completedByNamesSentence(row.doneBy)}</p>
        </details>
      ),
    },
    ...(projectsOn
      ? [{
        key: 'project',
        header: 'My project',
        fold: 2,
        sortValue: (row: ExecutiveMatterDto) => row.mine.project?.name ?? null,
        cell: (row: ExecutiveMatterDto) => (row.mine.project ? <ProjectChip project={row.mine.project} /> : <span className="muted" role="img" aria-label="Not filed">{EM_DASH}</span>),
      } satisfies DataTableColumn<ExecutiveMatterDto>]
      : []),
    {
      key: 'actions',
      header: <span className="visually-hidden">Actions</span>,
      name: 'Actions',
      sortable: false,
      align: 'end',
      width: '3rem',
      card: 'actions',
      cell: (row) => {
        const entries: MenuEntry[] = [
          row.mine.status === 'done'
            ? { id: 'reopen', label: 'Reopen', icon: 'undo', onSelect: () => void setStatus(row, 'open') }
            : { id: 'done', label: 'Mark as completed', icon: 'check', onSelect: () => void setStatus(row, 'done') },
          row.mine.status === 'dismissed'
            ? { id: 'restore', label: 'Restore', icon: 'undo', onSelect: () => void setStatus(row, 'open') }
            : { id: 'dismiss', label: 'Dismiss', icon: 'x', onSelect: () => void setStatus(row, 'dismissed') },
          ...(projectsOn ? [{ id: 'file', label: 'File under…', icon: 'folder' as const, onSelect: () => setFiling(row) }] : []),
          { id: 'edit', label: 'Edit…', icon: 'edit', onSelect: () => setEditing(row) },
          ...(row.canDelete ? [{ id: 'delete', label: 'Delete…', icon: 'trash' as const, danger: true, onSelect: () => setDeleting(row) }] : []),
        ];
        return (
          <Menu
            label={`Actions for “${row.text}”`}
            entries={entries}
            trigger={(triggerProps) => <IconButton icon="more-horizontal" label={`Actions for “${row.text}”`} {...triggerProps} loading={busy.has(row.id)} />}
          />
        );
      },
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [projectsOn, today, busy]);

  // Why the list is shown only for the filter it was read for (the stage 6 review; 04-data §1.3, 06-modules §5.8): a change
  // of filter keeps the previous filter's rows while the new read runs (`useRequest` keeps data across a change of its
  // key), and a failed read kept them for good — under the new filter's name in the footer. While the read for this filter
  // runs the table shows its loading rows, and a failed read shows the failure, whatever was read before.
  const failed = request.status === 'error' && request.error !== null;
  const readFor = useReadFor(request.data, filter, request.refreshing);
  const data = failed || readFor !== filter ? null : request.data;
  const empty = matterEmpty(filter);
  const showAll = (): void =>
    setParams((current) => {
      const next = new URLSearchParams(current);
      next.set('status', 'all');
      return next;
    }, { replace: true });
  return (
    <Page measure="wide" className="executive-matters">
      <PageHead
        title="Matters"
        subtitle="One shared list for every executive; each keeps their own word on each matter."
        actions={<Button variant="primary" icon="plus" onClick={() => setAdding(true)}>Add a matter</Button>}
        strip={<ExecutiveStrip />}
      />
      {failed && request.error ? (
        <div className="stack stack--3">
          <div className="executive-matters__toolbar">{filterControl}</div>
          <ExecutiveFailure failure={request.error} what="the shared list" onRetry={request.reload} />
        </div>
      ) : (
        <div className="stack stack--3">
          <div className="executive-matters__toolbar">{filterControl}</div>
          <DataTable
            caption="Shared matters"
            failureNoun="the shared matters"
            columns={columns}
            rows={data?.items ?? []}
            rowKey={(row) => row.id}
            status={!data ? { kind: 'loading', rowCount: 5 } : { kind: 'ready' }}
            sort={{ defaultSort: { key: 'priority', dir: 'asc' }, namespace: 'matters' }}
            clientSort
            empty={{ title: empty.title, description: empty.description, action: <Button variant="secondary" icon="plus" onClick={() => setAdding(true)}>Add a matter</Button> }}
            // Why every filter but All is a filter (the stage 6 review; 05-content §5: "A filtered empty state MUST say so and
            // offer to clear the filter"): Open, Completed and Dismissed each narrow the shared list; clearing shows All.
            filtered={{ active: filter !== 'all', title: empty.title, description: empty.description, onClear: showAll }}
            footer={
              <div className="audit-footer">
                <span>{data ? pluralise(data.items.length, { one: `# matter · ${MATTER_FILTER_LABELS[filter]}`, other: `# matters · ${MATTER_FILTER_LABELS[filter]}` }) : ''}</span>
                <LinkButton size="sm" variant="secondary" icon="download" href={`/api/executive/matters/export.csv${filter === 'all' ? '?status=all' : `?status=${filter}`}`} download>Export CSV</LinkButton>
              </div>
            }
          />
          <p className="text-sm secondary">{MANUAL_MATTER_NOTE}</p>
        </div>
      )}
      <AuthorLine />

      <AddMatterDialog
        open={adding}
        onClose={() => setAdding(false)}
        onAdded={(matter) => {
          setAdding(false);
          if (filter === 'open' || filter === 'all') request.setData((current) => (current ? { ...current, items: [matter, ...current.items] } : current));
          toast.success(`“${matter.text}” is on the shared list.`);
        }}
      />
      <EditMatterDialog
        matter={editing}
        onClose={() => setEditing(null)}
        onSaved={(matter) => {
          setEditing(null);
          replace(matter);
          toast.success(`“${matter.text}” updated.`);
        }}
      />
      <FileMatterDialog
        matter={filing}
        onClose={() => setFiling(null)}
        onFiled={(matter) => {
          setFiling(null);
          replace(matter);
          toast.success(matter.mine.project ? `“${matter.text}” filed under ${matter.mine.project.name} for you.` : `“${matter.text}” is no longer filed under a project.`);
        }}
      />
      <DeleteMatterDialog
        matter={deleting}
        onClose={() => setDeleting(null)}
        onDeleted={(matter) => {
          setDeleting(null);
          request.setData((current) => (current ? { ...current, items: current.items.filter((item) => item.id !== matter.id) } : current));
          toast.success(`Deleted “${matter.text}”.`);
        }}
      />
    </Page>
  );
}

const PRIORITY_OPTIONS = TODO_PRIORITIES.map((value) => ({ value, label: TODO_PRIORITY_LABELS[value] }));

function errorLine(failure: ApiFailure): string {
  return failureSentence(failure);
}

function AddMatterDialog({ open, onClose, onAdded }: { open: boolean; onClose: () => void; onAdded: (matter: ExecutiveMatterDto) => void }) {
  const [text, setText] = useState('');
  const [why, setWhy] = useState('');
  const [priority, setPriority] = useState<TodoPriority>('medium');
  const [dueDate, setDueDate] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [textError, setTextError] = useState<string | null>(null);
  useEffect(() => {
    if (!open) return;
    setText('');
    setWhy('');
    setPriority('medium');
    setDueDate('');
    setSaving(false);
    setError(null);
    setTextError(null);
  }, [open]);
  const submit = async (): Promise<void> => {
    if (text.trim() === '') {
      setTextError('Say what needs doing.');
      return;
    }
    setSaving(true);
    setError(null);
    const body: CreateExecutiveMatterRequest = { text: text.trim(), why: why.trim() === '' ? null : why.trim(), priority, dueDate: dueDate === '' ? null : dueDate };
    const result = await api.post<{ matter: ExecutiveMatterDto }>('/api/executive/matters', body);
    setSaving(false);
    if (!result.ok) {
      setError(errorLine(result));
      return;
    }
    onAdded(result.data.matter);
  };
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Add a matter"
      description={ADD_MATTER_DESCRIPTION}
      size="md"
      mobile="fullscreen"
      initialFocus="first"
      unsavedChanges={text.trim() !== '' || why.trim() !== ''}
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving} disabledReason={saving ? 'Wait for the matter to be added.' : undefined}>Cancel</Button>
          <Button variant="primary" onClick={() => void submit()} loading={saving}>Add matter</Button>
        </>
      }
    >
      <form className="stack stack--3" onSubmit={(event) => { event.preventDefault(); void submit(); }}>
        <FormErrorSummary errors={error ? [error] : []} />
        <TextField label="What needs doing" required maxLength={MATTER_TEXT_MAX} value={text} onChange={(event) => setText(event.target.value)} onBlur={() => setTextError(text.trim() === '' && text !== '' ? 'Say what needs doing.' : null)} error={textError} hint={`At most ${MATTER_TEXT_MAX} characters.`} />
        <TextareaField label="Why" value={why} maxLength={MATTER_TEXT_MAX} onChange={(event) => setWhy(event.target.value)} hint="Optional." />
        <SelectField label="Priority" value={priority} onChange={(event) => setPriority(event.target.value as TodoPriority)} options={PRIORITY_OPTIONS} />
        <TextField label="Due date" type="date" value={dueDate} onChange={(event) => setDueDate(event.target.value)} hint="Optional." />
      </form>
    </Dialog>
  );
}

function EditMatterDialog({ matter, onClose, onSaved }: { matter: ExecutiveMatterDto | null; onClose: () => void; onSaved: (matter: ExecutiveMatterDto) => void }) {
  const [text, setText] = useState('');
  const [priority, setPriority] = useState<TodoPriority>('medium');
  const [dueDate, setDueDate] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!matter) return;
    setText(matter.text);
    setPriority(matter.priority);
    setDueDate(matter.dueDate ?? '');
    setSaving(false);
    setError(null);
  }, [matter]);
  const changes: UpdateExecutiveMatterRequest = {};
  if (matter) {
    if (matter.canEditText && text.trim() !== '' && text.trim() !== matter.text) changes.text = text.trim();
    if (priority !== matter.priority) changes.priority = priority;
    if ((dueDate === '' ? null : dueDate) !== matter.dueDate) changes.dueDate = dueDate === '' ? null : dueDate;
  }
  const dirty = Object.keys(changes).length > 0;
  const save = async (): Promise<void> => {
    if (!matter || !dirty) return;
    setSaving(true);
    setError(null);
    const result = await api.patch<{ matter: ExecutiveMatterDto }>(`/api/executive/matters/${encodeURIComponent(matter.id)}`, changes);
    setSaving(false);
    if (!result.ok) {
      setError(errorLine(result));
      return;
    }
    onSaved(result.data.matter);
  };
  return (
    <Dialog
      open={matter !== null}
      onClose={onClose}
      title="Edit matter"
      description={EDIT_MATTER_DESCRIPTION}
      size="md"
      mobile="fullscreen"
      initialFocus="first"
      unsavedChanges={dirty && !saving}
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving} disabledReason={saving ? 'Wait for the changes to be saved.' : undefined}>Cancel</Button>
          <Button variant="primary" onClick={() => void save()} loading={saving} disabled={!dirty} disabledReason={!dirty ? 'Change something first.' : undefined}>Save changes</Button>
        </>
      }
    >
      <form className="stack stack--3" onSubmit={(event) => { event.preventDefault(); void save(); }}>
        <FormErrorSummary errors={error ? [error] : []} />
        <TextField label="What needs doing" maxLength={MATTER_TEXT_MAX} value={text} onChange={(event) => setText(event.target.value)} readOnly={!matter?.canEditText} hint={matter?.canEditText ? `At most ${MATTER_TEXT_MAX} characters.` : MATTER_TEXT_LOCKED} />
        <SelectField label="Priority" value={priority} onChange={(event) => setPriority(event.target.value as TodoPriority)} options={PRIORITY_OPTIONS} />
        <TextField label="Due date" type="date" value={dueDate} onChange={(event) => setDueDate(event.target.value)} hint="Optional; clear it to remove the date." />
      </form>
    </Dialog>
  );
}

const NO_PROJECT = '';

/** One filing per executive per matter: one of their own live projects, or none (design §2.7; `fileOwnMatter`). */
function FileMatterDialog({ matter, onClose, onFiled }: { matter: ExecutiveMatterDto | null; onClose: () => void; onFiled: (matter: ExecutiveMatterDto) => void }) {
  const [chosen, setChosen] = useState(NO_PROJECT);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!matter) return;
    setChosen(matter.mine.project?.id ?? NO_PROJECT);
    setSaving(false);
    setError(null);
  }, [matter]);
  const initial = matter?.mine.project?.id ?? NO_PROJECT;
  const dirty = chosen !== initial;
  const apply = async (): Promise<void> => {
    if (!matter || !dirty) return;
    setSaving(true);
    setError(null);
    const result = await api.patch<{ matter: ExecutiveMatterDto }>(`/api/executive/matters/${encodeURIComponent(matter.id)}/state`, { projectId: chosen === NO_PROJECT ? null : chosen } satisfies SetExecutiveMatterStateRequest);
    setSaving(false);
    if (!result.ok) {
      setError(errorLine(result));
      return;
    }
    onFiled(result.data.matter);
  };
  return (
    <Dialog
      open={matter !== null}
      onClose={onClose}
      title={matter ? `File “${matter.text}” under…` : 'File under…'}
      description="Your own filing: it puts the matter in one of your projects, and no other executive sees it."
      size="sm"
      initialFocus="first"
      unsavedChanges={dirty && !saving}
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving} disabledReason={saving ? 'Wait for the filing to be saved.' : undefined}>Cancel</Button>
          <Button variant="primary" onClick={() => void apply()} loading={saving} disabled={!dirty} disabledReason={!dirty ? 'Choose another project first.' : undefined}>Apply</Button>
        </>
      }
    >
      <div className="stack stack--3">
        <FormErrorSummary errors={error ? [error] : []} />
        {/* Why the list is read only while the dialog is open: the reader's projects are needed for this choice alone. */}
        {matter ? <OwnProjectSelect chosen={chosen} onChoose={setChosen} /> : null}
      </div>
    </Dialog>
  );
}

function OwnProjectSelect({ chosen, onChoose }: { chosen: string; onChoose: (projectId: string) => void }) {
  const projects = useRequest<ProjectListResponse>((signal) => api.get<ProjectListResponse>(OWN_PROJECTS_PATH, { signal }), []);
  if (projects.status === 'loading' && !projects.data) return <StateView kind="loading" label="Loading your projects" minHeight="4rem" compact />;
  // Why `failureDetail` and never the server's message raw (CLAUDE.md; 05-content §4).
  if (projects.status === 'error' && projects.error && !projects.data) return <p className="field__error" role="alert">We couldn’t load your projects. {failureDetail(projects.error)}</p>;
  const options = [{ value: NO_PROJECT, label: 'No project' }, ...(projects.data?.items ?? []).map((project) => ({ value: project.id, label: project.name }))];
  return <SelectField label="Project" value={chosen} onChange={(event) => onChoose(event.target.value)} options={options} />;
}

function DeleteMatterDialog({ matter, onClose, onDeleted }: { matter: ExecutiveMatterDto | null; onClose: () => void; onDeleted: (matter: ExecutiveMatterDto) => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (matter) {
      setBusy(false);
      setError(null);
    }
  }, [matter]);
  return (
    <ConfirmDialog
      open={matter !== null}
      title={matter ? `Delete “${matter.text}”?` : 'Delete this matter?'}
      confirmLabel="Delete"
      danger
      busy={busy}
      error={error}
      onCancel={onClose}
      onConfirm={async () => {
        if (!matter) return;
        setBusy(true);
        const result = await api.delete<unknown>(`/api/executive/matters/${encodeURIComponent(matter.id)}`);
        setBusy(false);
        if (!result.ok) {
          setError(errorLine(result));
          return;
        }
        onDeleted(matter);
      }}
    >
      <p>It leaves the shared list for every executive, with each one’s own word on it. This cannot be undone.</p>
    </ConfirmDialog>
  );
}
